/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/UsersModern.tsx
 * PURPOSE: Modern user management với Vietnamese F&B roles - Tenant Admin feature
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * DATE: 2026-01-15
 * COMPLIANCE: SDLC 6.3.1 + Zero Mock Policy + NQH-BRAND-DESIGN-SYSTEM.md
 *
 * @business-context
 * - Domain: Vietnamese F&B Platform, User Management, Tenant Admin
 * - Impact: User Onboarding, Access Control, Team Management
 * - Stakeholders: Tenant Admins, Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Page, API Integration, Modal Form Pattern
 * - Dependencies: React 18, React Router, userApi, CreateUserModal
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - ADR: docs/02-design/18-adrs/ADR-frontend-auth-state-management.md
 * - API: POST /api/v1/users/, GET /api/v1/users/
 * - Backend: backend/app/api/v1/users.py
 * - GAP Report: docs/08-collaborate/02-Progress-Reports/Pilot-Onboarding/USER-CREATION-GAP-REPORT.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { userApi } from '../services/api/user';
import { CreateUserModal } from '../components/users/CreateUserModal';
import { UserResponse, UserRole } from '../types/user';
import styles from './UsersModern.module.css';

const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Quản trị viên',
  MANAGER: 'Quản lý',
  SUPERVISOR: 'Giám sát',
  EMPLOYEE: 'Nhân viên',
  VIEWER: 'Xem',
};

export const UsersModern: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, accessToken, logout } = useAuthStore();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    if (!accessToken) {
      setError('Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await userApi.getAllUsers(accessToken, 0, 100, searchQuery || undefined);
      setUsers(response.users || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Không thể tải danh sách nhân viên';
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, searchQuery]);

  // Initial load
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle user creation success
  const handleUserCreated = useCallback((newUser: UserResponse) => {
    // Add new user to list without refetching
    setUsers(prev => [newUser, ...prev]);
    setShowAddUser(false);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.phone_number || '').includes(searchQuery)
  );

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
      case 'SUPER_ADMIN':
        return `${styles.roleBadge} ${styles.roleAdmin}`;
      case 'MANAGER':
      case 'SUPERVISOR':
        return `${styles.roleBadge} ${styles.roleManager}`;
      case 'EMPLOYEE':
      case 'VIEWER':
        return `${styles.roleBadge} ${styles.roleEmployee}`;
      default:
        return styles.roleBadge;
    }
  };

  const getRoleText = (role: UserRole) => {
    return ROLE_LABELS[role] || role;
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          {/* Logo & Brand */}
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className={styles.logoButton}
            >
              <div className={styles.logoIcon}>
                <span className={styles.logoText}>N</span>
              </div>
              <h1 className={styles.brandName}>
                NQH-Bot Platform
              </h1>
            </button>
          </div>

          {/* User Info & Actions */}
          <div className={styles.headerRight}>
            <div className={styles.userInfo}>
              {currentUser?.full_name || 'User'}
            </div>
            <button
              onClick={handleLogout}
              className={styles.logoutButton}
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Page Title */}
        <div className={styles.pageHeader}>
          <h2 className={styles.pageTitle}>
            👥 Quản lý Nhân viên
          </h2>
          <p className={styles.pageSubtitle}>
            Quản lý danh sách nhân viên và phân quyền hệ thống
          </p>
        </div>

        {/* Search and Actions */}
        <div className={styles.searchCard}>
          <div className={styles.searchWrapper}>
            <input
              type="text"
              aria-label="Tìm kiếm nhân viên"
              placeholder="Tìm theo tên, email hoặc số điện thoại..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <button
            onClick={() => setShowAddUser(true)}
            className={styles.addButton}
          >
            <span>➕</span>
            Thêm nhân viên
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className={styles.tableCard}>
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>⚠️</span>
              <h3 className={styles.emptyTitle}>Có lỗi xảy ra</h3>
              <p className={styles.emptyText}>{error}</p>
              <button
                onClick={fetchUsers}
                className={styles.addButton}
                style={{ marginTop: '1rem' }}
              >
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className={styles.tableCard}>
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p className={styles.loadingText}>
                Đang tải danh sách nhân viên...
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.tableCard}>
            {filteredUsers.length === 0 ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>👥</span>
                <h3 className={styles.emptyTitle}>
                  Không tìm thấy nhân viên
                </h3>
                <p className={styles.emptyText}>
                  {searchQuery ? 'Không có nhân viên nào phù hợp với tìm kiếm của bạn.' : 'Chưa có nhân viên nào trong hệ thống.'}
                </p>
              </div>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead className={styles.tableHead}>
                    <tr>
                      <th className={styles.th}>👤 Họ và tên</th>
                      <th className={styles.th}>📧 Email</th>
                      <th className={styles.th}>📱 Điện thoại</th>
                      <th className={styles.th}>🎭 Vai trò</th>
                      <th className={`${styles.th} ${styles.thCenter}`}>📊 Trạng thái</th>
                      <th className={`${styles.th} ${styles.thCenter}`}>⚙️ Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        className={`${styles.tr} ${index % 2 === 0 ? styles.trEven : ''}`}
                      >
                        <td className={`${styles.td} ${styles.userName}`}>
                          {user.full_name}
                        </td>
                        <td className={`${styles.td} ${styles.userDetail}`}>
                          {user.email}
                        </td>
                        <td className={`${styles.td} ${styles.userDetail}`}>
                          {user.phone_number}
                        </td>
                        <td className={styles.td}>
                          <span className={getRoleBadgeClass(user.role)}>
                            {getRoleText(user.role)}
                          </span>
                        </td>
                        <td className={`${styles.td} ${styles.tdCenter}`}>
                          <span className={`${styles.statusBadge} ${user.is_active ? styles.statusActive : styles.statusInactive}`}>
                            {user.is_active ? '✅ Hoạt động' : '❌ Vô hiệu'}
                          </span>
                        </td>
                        <td className={`${styles.td} ${styles.tdCenter}`}>
                          <div className={styles.actionButtons}>
                            <button className={styles.editButton}>
                              ✏️
                            </button>
                            <button className={styles.deleteButton}>
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Statistics */}
        <div className={styles.statsGrid}>
          <div className={styles.statsCard}>
            <div className={`${styles.statsIcon} ${styles.statsIconSuccess}`}>
              <span>👥</span>
            </div>
            <div>
              <h3 className={styles.statsLabel}>
                Tổng nhân viên
              </h3>
              <p className={styles.statsValue}>
                {users.length}
              </p>
            </div>
          </div>

          <div className={styles.statsCard}>
            <div className={`${styles.statsIcon} ${styles.statsIconInfo}`}>
              <span>✅</span>
            </div>
            <div>
              <h3 className={styles.statsLabel}>
                Đang hoạt động
              </h3>
              <p className={styles.statsValue}>
                {users.filter(u => u.is_active).length}
              </p>
            </div>
          </div>

          <div className={styles.statsCard}>
            <div className={`${styles.statsIcon} ${styles.statsIconWarning}`}>
              <span>🎭</span>
            </div>
            <div>
              <h3 className={styles.statsLabel}>
                Quản lý
              </h3>
              <p className={styles.statsValue}>
                {users.filter(u => u.role === 'MANAGER' || u.role === 'ADMIN' || u.role === 'SUPERVISOR').length}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.footerText}>
            🍽️ Hệ thống quản lý F&B thông minh cho doanh nghiệp Việt Nam
          </p>
          <p className={styles.footerCopyright}>
            © 2025 NQH-Bot Platform - Powered by Nhat Quang Holding
          </p>
        </div>
      </main>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showAddUser}
        onClose={() => setShowAddUser(false)}
        onSuccess={handleUserCreated}
      />
    </div>
  );
};

export default UsersModern;
